import { useEffect, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useSettings } from '@/store/settings';
import { BackgroundDice } from '@/components/DiceField';
import { TooltipProvider } from '@/components/ui/tooltip';
import { sound } from '@/lib/sound';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/', label: 'Play' },
  { to: '/rules', label: 'Rules' },
  { to: '/stats', label: 'Stats' },
  { to: '/settings', label: 'Settings' },
] as const;

export function AppShell({ children }: { children: ReactNode }) {
  const theme = useSettings(s => s.theme);
  const diceTheme = useSettings(s => s.diceTheme);
  const motion = useSettings(s => s.motion);
  const backgroundDice = useSettings(s => s.backgroundDice);
  const pathname = useRouterState({ select: s => s.location.pathname });
  const inGame = pathname.startsWith('/play');

  useEffect(() => {
    const el = document.documentElement;
    el.dataset.theme = theme;
    el.dataset.dice = diceTheme;
    el.dataset.motion = motion;
  }, [theme, diceTheme, motion]);

  useEffect(() => {
    const unlock = () => sound.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="relative flex min-h-dvh flex-col bg-bg text-text">
        {backgroundDice && <BackgroundDice />}
        <header
          className={cn(
            inGame
              ? 'static border-b border-transparent bg-transparent'
              : 'sticky top-0 z-40 border-b border-line bg-bg/85 backdrop-blur'
          )}
        >
          <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-4">
            <Link
              to="/"
              className="font-display text-xl font-extrabold tracking-tight text-text"
              aria-label="yAzzI home"
            >
              y<span className="text-accent [text-shadow:var(--glow)]">A</span>
              zz
              <span className="text-accent [text-shadow:var(--glow)]">I</span>
            </Link>
            {!inGame && (
              <nav aria-label="Main" className="flex items-center gap-1">
                {NAV.map(n => (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="rounded-full px-3 py-1.5 text-sm font-semibold text-muted transition-colors hover:text-text"
                    activeProps={{ className: 'bg-surface-2 text-text' }}
                    activeOptions={{ exact: n.to === '/' }}
                  >
                    {n.label}
                  </Link>
                ))}
              </nav>
            )}
          </div>
        </header>
        <main className="relative z-[1] flex-1">{children}</main>
      </div>
    </TooltipProvider>
  );
}
